import { useEffect, useState } from 'react';
import { locationManager, cloudStorage } from '@telegram-apps/sdk-react';

import ProfileController from '@/api/profile/profile.ts';
import { useDiscoveryStore } from '@/store/useDiscoveryStore.ts';
import { useBrowserGeolocationControl } from '@/hooks/useBrowserGeolocationControl.tsx';
import { catchErrors } from '@/utils/sentry-monitor.tsx';
import { GeolocationCloudKeys } from '@/enums/storageEnums.ts';

const MODAL_OPEN_LIFETIME = 24 * 60 * 60 * 1000; // 24 hours for modal openings

export const useGeolocationControl = () => {
  const {
    isGeoAccessRequested,
    isGeoAccessGranted,
    geoAccessRequested,
    geoAccessGranted
  } = useDiscoveryStore();

  const [geoModalOpen, setGeoModalOpen] = useState<boolean>(false);
  const [isRequestingTelegramLocation, setIsRequestingTelegramLocation] =
    useState<boolean>(false);

  const closeGeoModal = () => setGeoModalOpen(false);
  const openGeoModal = async () => {
    // Check if modal was opened recently
    let lastModalOpenedAt: number | null = null;
    if (cloudStorage.getItem.isAvailable()) {
      try {
        const storedValue = await cloudStorage.getItem(
          GeolocationCloudKeys.TELEGRAM_GEO_ACCESS_MODAL_KEY
        );
        if (storedValue) {
          const parsedDate = new Date(storedValue);
          const parsedTimestamp = parsedDate.getTime();
          if (!isNaN(parsedTimestamp) && parsedTimestamp > 0) {
            lastModalOpenedAt = parsedTimestamp;
          }
        }
      } catch (e) {
        /* empty */
      }
    }

    // Skip opening modal if within MODAL_OPEN_LIFETIME
    if (lastModalOpenedAt !== null) {
      const now = Date.now();
      if (now - lastModalOpenedAt < MODAL_OPEN_LIFETIME) {
        return;
      }
    }

    // Open modal and store timestamp
    setGeoModalOpen(true);
    if (cloudStorage.setItem.isAvailable()) {
      try {
        const now = new Date().toISOString();
        await cloudStorage.setItem(
          GeolocationCloudKeys.TELEGRAM_GEO_ACCESS_MODAL_KEY,
          now
        );
      } catch (e) {
        /* error */
      }
    }
  };

  const isTelegramGeoSupported = locationManager.isSupported();

  // Use browser geolocation only if Telegram's locationManager is not supported
  const { position, error, loading } = useBrowserGeolocationControl(
    isTelegramGeoSupported
  );

  useEffect(() => {
    const checkAndRequestLocation = async () => {
      // Don't request if already granted, currently requesting, or already requested and denied
      if (
        isGeoAccessGranted ||
        isRequestingTelegramLocation ||
        isGeoAccessRequested
      ) {
        return;
      }

      if (isTelegramGeoSupported) {
        const fetchTelegramLocation = async () => {
          setIsRequestingTelegramLocation(true);
          try {
            const location = (await locationManager.requestLocation()) as {
              latitude: number;
              longitude: number;
            };

            await ProfileController.updateCoordinates({
              coordinates: { lat: location.latitude, lng: location.longitude }
            });

            geoAccessGranted(true);
          } catch (e) {
            // Mark as requested to prevent infinite loop on Android
            // when Telegram doesn't have geolocation permissions
            geoAccessRequested(true);
            await openGeoModal();
          } finally {
            setIsRequestingTelegramLocation(false);
          }
        };

        await fetchTelegramLocation();
        return;
      }

      // Browser geolocation logic
      if (loading) {
        return;
      }

      if (position) {
        try {
          await ProfileController.updateCoordinates({
            coordinates: { lat: position.latitude, lng: position.longitude }
          });
          geoAccessGranted(true);
        } catch (e) {
          catchErrors(e);
        }
      } else if (error && !isGeoAccessRequested) {
        geoAccessRequested(true);
      }
    };

    void checkAndRequestLocation();

    return () => {
      // Cleanup if needed (e.g., abort ongoing requests)
    };
  }, [
    position,
    error,
    loading,
    isGeoAccessGranted,
    isTelegramGeoSupported,
    isGeoAccessRequested,
    isRequestingTelegramLocation
  ]);

  return {
    geoModalOpen,
    openGeoModal,
    closeGeoModal,
    isGeoAccessGranted,
    isGeoAccessRequested,
    position,
    geolocationError: error,
    geolocationLoading: loading
  };
};
